import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type DrillDimension = 'Employee' | 'Manager' | 'Engagement' | 'Client' | 'EngagementType' | 'Activity' | 'ChargeType' | 'WorkDate' | 'Entry';

export interface DrillFilter {
  dimension: DrillDimension;
  value: string;
  label: string;
}

export interface DrillState {
  rootGroupBy: DrillDimension;
  breadcrumbs: DrillFilter[];
  currentGroupBy: DrillDimension;
}

interface DrillContextValue {
  state: DrillState;
  drillIn: (dimension: DrillDimension, value: string, label: string, nextGroupBy: DrillDimension) => void;
  popTo: (index: number) => void;
  changeDrillBy: (dimension: DrillDimension) => void;
  reset: () => void;
  availableDimensions: DrillDimension[];
  /** Build the filter record from breadcrumbs for use in API queries */
  drillFilters: Record<string, string>;
}

const DrillCtx = createContext<DrillContextValue | null>(null);

// Map dimension name to the query param key used by DrillQueryParams
const DIMENSION_TO_FILTER_KEY: Record<string, string> = {
  Employee: 'employeeId',
  Manager: 'managerId',
  Engagement: 'engagementId',
  Client: 'clientId',
  EngagementType: 'engagementTypeId',
  Activity: 'activityId',
  ChargeType: 'chargeTypeKey',
  WorkDate: 'workDate',
};

interface DrillProviderProps {
  rootGroupBy: DrillDimension;
  allDimensions: DrillDimension[];
  defaultDrillPath: DrillDimension[];
  children: React.ReactNode;
}

export const DrillProvider: React.FC<DrillProviderProps> = ({
  rootGroupBy,
  allDimensions,
  defaultDrillPath,
  children,
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState<DrillFilter[]>([]);
  const [currentGroupBy, setCurrentGroupBy] = useState<DrillDimension>(rootGroupBy);

  const drillIn = useCallback((dimension: DrillDimension, value: string, label: string, nextGroupBy: DrillDimension) => {
    setBreadcrumbs(prev => [...prev, { dimension, value, label }]);
    setCurrentGroupBy(nextGroupBy);
  }, []);

  const popTo = useCallback((index: number) => {
    setBreadcrumbs(prev => prev.slice(0, index));
    // When popping back, determine the groupBy: the dimension that was being viewed at that level.
    // If index is 0 (home), use rootGroupBy. Otherwise use defaultDrillPath[index] or the next unused dimension.
    if (index <= 0) {
      setCurrentGroupBy(rootGroupBy);
    } else {
      // Use default path if available at that depth, otherwise keep current
      const nextDefault = defaultDrillPath[index] ?? rootGroupBy;
      setCurrentGroupBy(nextDefault);
    }
  }, [rootGroupBy, defaultDrillPath]);

  const changeDrillBy = useCallback((dimension: DrillDimension) => {
    setCurrentGroupBy(dimension);
  }, []);

  const reset = useCallback(() => {
    setBreadcrumbs([]);
    setCurrentGroupBy(rootGroupBy);
  }, [rootGroupBy]);

  // Available dimensions = all minus those already used in breadcrumbs (and minus the current groupBy)
  const usedDimensions = useMemo(() => new Set(breadcrumbs.map(b => b.dimension)), [breadcrumbs]);
  const availableDimensions = useMemo(
    () => allDimensions.filter(d => !usedDimensions.has(d) && d !== currentGroupBy),
    [allDimensions, usedDimensions, currentGroupBy],
  );

  // Build filter record from breadcrumbs for API query params
  const drillFilters = useMemo(() => {
    const filters: Record<string, string> = {};
    breadcrumbs.forEach(b => {
      const key = DIMENSION_TO_FILTER_KEY[b.dimension];
      if (key) filters[key] = b.value;
    });
    return filters;
  }, [breadcrumbs]);

  const state = useMemo<DrillState>(
    () => ({ rootGroupBy, breadcrumbs, currentGroupBy }),
    [rootGroupBy, breadcrumbs, currentGroupBy],
  );

  const value = useMemo<DrillContextValue>(
    () => ({ state, drillIn, popTo, changeDrillBy, reset, availableDimensions, drillFilters }),
    [state, drillIn, popTo, changeDrillBy, reset, availableDimensions, drillFilters],
  );

  return <DrillCtx.Provider value={value}>{children}</DrillCtx.Provider>;
};

export const useDrill = (): DrillContextValue => {
  const ctx = useContext(DrillCtx);
  if (!ctx) throw new Error('useDrill must be used within a DrillProvider');
  return ctx;
};

export default DrillCtx;
